import React, { useEffect, useMemo, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtNum } from "../lib/format";
import { FileSignature, Search, X, Eye, Trash2, AlertTriangle, Wand2, FilePlus, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { useConfirm } from "../components/ConfirmDialog";
import { PurgeTestInvoicesDialog } from "../components/invoices/PurgeTestInvoicesDialog";
import RetroactiveInvoiceDialog from "../components/invoices/RetroactiveInvoiceDialog";
import { toast } from "sonner";

const STATUS_META = {
  BROUILLON: { label: "Brouillon", className: "bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0]" },
  ENVOYEE: { label: "Envoyée", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  PAYEE: { label: "Payée", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  PARTIELLE: { label: "Partielle", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  EN_RETARD: { label: "En retard", className: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
  ANNULEE: { label: "Annulée", className: "bg-[#F0F0EB] text-[#A3A6A3] border-[#E5E5E0]" },
};

export default function Invoices() {
  const confirm = useConfirm();
  const [invoices, setInvoices] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  // Phase 96e — Multi-sélection admin pour suppression en masse
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Phase 101fv — Purge factures de test
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  // Phase 101gd — Import rétroactif factures papier (Chermignon, Oseo, etc.)
  const [retroDialogOpen, setRetroDialogOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visible) => {
    setSelectedIds((prev) => {
      const allSelected = visible.every((it) => prev.has(it.id));
      if (allSelected) return new Set();
      return new Set(visible.map((it) => it.id));
    });
  };
  const bulkForceDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await confirm({
      title: `Supprimer ${selectedIds.size} facture(s) sélectionnée(s) ?`,
      description: "Action IRRÉVERSIBLE. Uniquement les factures en statut BROUILLON ou ANNULEE seront supprimées. Les paiements liés (si présents) seront marqués voided pour la traçabilité comptable, et les devis convertis liés seront ré-ouverts en ACCEPTE.",
      confirmText: "Oui, supprimer",
      destructive: true,
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/admin/invoices/bulk-force-delete", { ids: Array.from(selectedIds) });
      const refused = r.data.refused_by_status || {};
      const refusedText = Object.entries(refused).map(([s, n]) => `${n} en ${s}`).join(", ");
      const parts = [`${r.data.deleted} supprimées`];
      if (r.data.voided_payments) parts.push(`${r.data.voided_payments} paiements voidés`);
      if (r.data.reopened_quotes) parts.push(`${r.data.reopened_quotes} devis ré-ouverts`);
      if (refusedText) parts.push(`Refusées : ${refusedText}`);
      toast.success(parts.join(" · "));
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression en masse");
    } finally {
      setBulkDeleting(false);
    }
  };

  const load = async () => {
    const [i, o, s] = await Promise.all([
      api.get("/invoices"),
      api.get("/invoices/overdue/list"),
      api.get("/invoices/summary").catch(() => ({ data: null })),
    ]);
    setInvoices(i.data);
    setOverdue(o.data);
    setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (inv) => {
    const ok = await confirm({
      title: "Supprimer cette facture ?",
      description: `La facture ${inv.reference} sera supprimée définitivement.`,
      destructive: true,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Facture supprimée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const filtered = invoices.filter((i) => {
    if (filterStatus !== "all" && i.status !== filterStatus) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (i.reference || "").toLowerCase().includes(q) || (i.debtor_name || "").toLowerCase().includes(q);
  });

  // Phase 101gd (Feb 2026) — Tri configurable par colonne.
  // Cas remonté par Alain : facture backdatée à juin 2026 apparaissait en tête
  // car tri par created_at DESC. Maintenant tri par défaut = issue_date DESC.
  const [sortBy, setSortBy] = useState("issue_date");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
  };
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = a[sortBy], vb = b[sortBy];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const SortHead = ({ col, children, className = "" }) => (
    <TableHead className={`cursor-pointer select-none hover:bg-[#F5F9F6] ${className}`} onClick={() => toggleSort(col)} data-testid={`sort-${col}`}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === col && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </TableHead>
  );

  // KPI
  const today = new Date().toISOString().slice(0, 10).slice(0, 7);
  const kpiTotalMonth = invoices
    .filter((i) => (i.issue_date || "").slice(0, 7) === today && i.status !== "ANNULEE")
    .reduce((acc, i) => acc + (i.total_ttc || 0), 0);
  const kpiUnpaid = invoices
    .filter((i) => i.status !== "PAYEE" && i.status !== "ANNULEE" && i.status !== "BROUILLON")
    .reduce((acc, i) => acc + ((i.total_ttc || 0) - (i.paid_amount || 0)), 0);
  const kpiOverdueAmount = overdue.reduce((acc, i) => acc + ((i.total_ttc || 0) - (i.paid_amount || 0)), 0);

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A]">Factures</h1>
          <p className="text-sm text-[#575A57]">Factures suisses avec QR-Bill ISO 20022 (générer depuis une commande client)</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-[#1F4E3D] hover:bg-[#16382C]"
              onClick={() => setRetroDialogOpen(true)}
              data-testid="btn-import-legacy-invoice"
            >
              <FilePlus className="w-4 h-4 mr-2" />
              Import facture papier
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
              onClick={() => setPurgeDialogOpen(true)}
              data-testid="btn-purge-test-invoices"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Purger factures de test
            </Button>
          </div>
        )}
      </div>

      {retroDialogOpen && (
        <RetroactiveInvoiceDialog
          open={retroDialogOpen}
          onClose={() => setRetroDialogOpen(false)}
          onCreated={() => { setRetroDialogOpen(false); load(); }}
        />
      )}

      {purgeDialogOpen && (
        <PurgeTestInvoicesDialog
          open={purgeDialogOpen}
          onClose={() => setPurgeDialogOpen(false)}
          onPurged={() => { setPurgeDialogOpen(false); load(); }}
        />
      )}

      {/* Phase 101fo — Bandeau balance globale (aperçu financier immédiat) */}
      {summary && (
        <Card
          className="border-[#C5DBCF] bg-gradient-to-br from-[#F5F9F6] via-[#FBF4E9] to-[#F5F9F6]"
          data-testid="invoices-balance-header"
        >
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div className="md:col-span-1 md:border-r md:border-[#C5DBCF] md:pr-5">
                <div className="text-[11px] uppercase tracking-wider text-[#575A57]">Solde ouvert clients</div>
                <div
                  className={`font-mono text-3xl mt-1 leading-tight ${
                    summary.balance_open > 0.01 ? "text-[#1F4E3D]" : summary.balance_open < -0.01 ? "text-[#8C3A2A]" : "text-[#1A1C1A]"
                  }`}
                  data-testid="invoices-balance-open"
                >
                  {fmtNum(summary.balance_open)} CHF
                </div>
                <div className="text-[11px] text-[#A3A6A3] mt-0.5">
                  {summary.balance_open > 0.01
                    ? "à encaisser (factures - paiements - avoirs)"
                    : summary.balance_open < -0.01
                    ? "avoirs en faveur des clients"
                    : "compte à l'équilibre"}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A6A3]">Total facturé</div>
                <div className="font-mono text-xl text-[#1A1C1A] mt-1" data-testid="invoices-total-invoiced">
                  {fmtNum(summary.total_invoiced)} CHF
                </div>
                <div className="text-[11px] text-[#A3A6A3] mt-0.5">
                  {summary.invoices_count} facture{summary.invoices_count > 1 ? "s" : ""} active{summary.invoices_count > 1 ? "s" : ""}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A6A3]">Total encaissé</div>
                <div className="font-mono text-xl text-[#1F4E3D] mt-1" data-testid="invoices-total-paid">
                  {fmtNum(summary.total_paid)} CHF
                </div>
                <div className="text-[11px] text-[#A3A6A3] mt-0.5">
                  {summary.total_invoiced > 0
                    ? `${Math.round((summary.total_paid / summary.total_invoiced) * 100)}% du facturé`
                    : "aucun paiement"}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A6A3]">Avoirs actifs</div>
                <div className="font-mono text-xl text-[#8C6B2F] mt-1" data-testid="invoices-total-credited">
                  {fmtNum(summary.total_credited)} CHF
                </div>
                <div className="text-[11px] text-[#A3A6A3] mt-0.5">
                  <Link to="/notes-de-credit" className="hover:underline">
                    {summary.credit_notes_count} note{summary.credit_notes_count > 1 ? "s" : ""} de crédit
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">CA facturé du mois</div>
          <div className="font-mono text-2xl text-[#1F4E3D] mt-1">{fmtNum(kpiTotalMonth)} CHF</div>
        </CardContent></Card>
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Encours total</div>
          <div className="font-mono text-2xl text-[#1A1C1A] mt-1">{fmtNum(kpiUnpaid)} CHF</div>
        </CardContent></Card>
        <Card className={`border-[#E5E5E0] ${overdue.length > 0 ? "ring-1 ring-[#E8C1B4]" : ""}`}><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3] flex items-center gap-1">
            {overdue.length > 0 && <AlertTriangle className="w-3 h-3 text-[#C05C47]" />} En retard
          </div>
          <div className={`font-mono text-2xl mt-1 ${overdue.length > 0 ? "text-[#C05C47]" : "text-[#1A1C1A]"}`}>
            {fmtNum(kpiOverdueAmount)} CHF
          </div>
          <div className="text-xs text-[#A3A6A3]">{overdue.length} facture{overdue.length > 1 ? "s" : ""}</div>
        </CardContent></Card>
      </div>

      {overdue.length > 0 && (
        <Card className="border-[#E8C1B4] bg-[#FCEEEA]" data-testid="overdue-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-[#C05C47]" />
              <span className="text-sm font-medium text-[#C05C47]">Factures en retard de paiement</span>
            </div>
            <div className="space-y-1">
              {overdue.map((i) => (
                <Link key={i.id} to={`/factures/${i.id}`} className="block text-xs hover:underline">
                  <span className="font-mono">{i.reference}</span> · {i.debtor_name} · échue le {fmtDate(i.due_date)} ·
                  <span className="font-mono ml-1 text-[#C05C47]">{((i.total_ttc || 0) - (i.paid_amount || 0)).toFixed(2)} CHF</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#A3A6A3] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input type="text" placeholder="Rechercher référence ou client…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 pr-8" data-testid="invoice-search" />
          {search && <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A3A6A3]"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Bandeau bulk actions (admin) */}
      {isAdmin && selectedIds.size > 0 && (
        <Card className="border-[#E8C1B4] bg-[#FCEEEA]" data-testid="invoice-bulk-actions-bar">
          <CardContent className="py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-[#8C3A2A]">
              <AlertTriangle className="w-4 h-4" />
              <span><strong>{selectedIds.size}</strong> facture{selectedIds.size > 1 ? "s" : ""} sélectionnée{selectedIds.size > 1 ? "s" : ""}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="invoice-clear-selection">Désélectionner</Button>
              <Button size="sm" variant="destructive" onClick={bulkForceDelete} disabled={bulkDeleting} data-testid="invoice-bulk-delete">
                <Trash2 className="w-4 h-4 mr-1" />
                {bulkDeleting ? "Suppression…" : `Supprimer ${selectedIds.size}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {isAdmin && (
                  <TableHead className="w-8">
                    <input
                      type="checkbox"
                      checked={sorted.length > 0 && sorted.every((it) => selectedIds.has(it.id))}
                      onChange={() => toggleSelectAll(sorted)}
                      className="cursor-pointer accent-[#1F4E3D]"
                      data-testid="invoice-select-all"
                    />
                  </TableHead>
                )}
                <SortHead col="reference">N° Facture</SortHead>
                <SortHead col="issue_date">Date</SortHead>
                <SortHead col="delivery_date">Livraison</SortHead>
                <SortHead col="due_date">Échéance</SortHead>
                <SortHead col="debtor_name">Client</SortHead>
                <SortHead col="total_ttc" className="text-right">Montant TTC</SortHead>
                <SortHead col="paid_amount" className="text-right">Payé</SortHead>
                <SortHead col="status">Statut</SortHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-12 text-[#A3A6A3]">
                  <FileSignature className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  Aucune facture. Créez-en une depuis une commande client.
                </TableCell></TableRow>
              ) : sorted.map((i) => {
                const meta = STATUS_META[i.status] || STATUS_META.BROUILLON;
                const remaining = (i.total_ttc || 0) - (i.paid_amount || 0);
                return (
                  <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
                    {isAdmin && (
                      <TableCell className="w-8">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(i.id)}
                          onChange={() => toggleSelect(i.id)}
                          className="cursor-pointer accent-[#1F4E3D]"
                          data-testid={`invoice-select-${i.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-sm">
                      <Link to={`/factures/${i.id}`} className="text-[#1F4E3D] hover:underline">{i.reference}</Link>
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(i.issue_date)}</TableCell>
                    <TableCell className="text-xs text-[#575A57]" data-testid={`invoice-delivery-date-${i.id}`}>{i.delivery_date ? fmtDate(i.delivery_date) : "—"}</TableCell>
                    <TableCell className="text-xs">{fmtDate(i.due_date)}</TableCell>
                    <TableCell><AccountLink resellerId={i.reseller_id} distributorId={i.distributor_id} compact testid={`inv-account-${i.id}`}>{i.debtor_name}</AccountLink></TableCell>
                    <TableCell className="text-right font-mono">{(i.total_ttc || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono">{(i.paid_amount || 0).toFixed(2)}{remaining > 0.01 && <div className="text-[10px] text-[#A3A6A3]">reste {remaining.toFixed(2)}</div>}</TableCell>
                    <TableCell><Badge className={meta.className}>{meta.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Link to={`/factures/${i.id}`}><Button variant="ghost" size="icon" data-testid={`view-invoice-${i.id}`}><Eye className="w-4 h-4" /></Button></Link>
                      <Button variant="ghost" size="icon" onClick={() => remove(i)} disabled={!["BROUILLON", "ANNULEE"].includes(i.status) || (i.paid_amount || 0) > 0} data-testid={`delete-invoice-${i.id}`}>
                        <Trash2 className="w-4 h-4 text-[#C05C47]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
